import React, { useState, useEffect } from 'react';
import { Wrench, Store, Building2, Gem, LogOut } from 'lucide-react';
import { API_URL, getServerUrl } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function ContextSelector({ onSelectContext }) {
  const { user, logout } = useAuth();
  const [selectedContext, setSelectedContext] = useState(null);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  const [boutiques, setBoutiques] = useState([]);
  const [nomEntreprise, setNomEntreprise] = useState('');
  const [slogan, setSlogan] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/boutiques`).then(r => r.json())
      .then(data => { if (Array.isArray(data)) setBoutiques(data); }).catch(err => console.error('Erreur:', err));
    fetch(`${API_URL}/parametres/bijouterie`).then(r => r.json())
      .then(d => {
        if (d?.nom_entreprise) setNomEntreprise(d.nom_entreprise);
        if (d?.slogan) setSlogan(d.slogan);
        if (d?.logo_url) setLogoUrl(d.logo_url);
      }).catch(err => console.error('Erreur:', err));
  }, []);

  const allContexts = [
    { id: 'atelier', name: 'Atelier', icon: Wrench, desc: 'Fabrication & Production', accent: '#B8860B' },
    { id: 'central', name: 'Central', icon: Building2, desc: 'Gestion & Administration', accent: '#8B7355' },
    { id: 'boutique', name: 'Boutique', icon: Store, desc: 'Vente & Service client', accent: '#C0A060' },
  ];
  // Auto-redirect pour rôles fixes (pas besoin de choisir)
  useEffect(() => {
    if (user?.role === 'ouvrier') {
      onSelectContext({ type: 'ouvrier', boutique_id: null, boutique_nom: 'Espace Ouvrier' });
    } else if (user?.role === 'chef_atelier' || user?.role === 'atelier') {
      onSelectContext({ type: 'atelier', boutique_id: null, boutique_nom: 'Atelier' });
    } else if (user?.role === 'boutique') {
      onSelectContext({ type: 'boutique', boutique_id: user.boutique_id || null, boutique_nom: user.boutique_nom || 'Boutique' });
    }
  }, [user, onSelectContext]);

  // Tous les contextes sont toujours visibles, mais désactivés selon le rôle
  const isDisabled = (ctxId) => {
    if (!user || user.role === 'admin') return false;
    if (user.role === 'atelier' || user.role === 'chef_atelier') return ctxId !== 'atelier';
    if (user.role === 'boutique') return ctxId !== 'boutique';
    return false;
  };
  const contexts = allContexts;

  const boutiquesList = boutiques.filter(b =>
    b.type === 'boutique' || (!b.type && b.nom && !b.nom.toLowerCase().includes('central') && !b.nom.toLowerCase().includes('atelier'))
  );
  const centralBoutique = boutiques.find(b => b.type === 'central' || b.nom?.toLowerCase().includes('central'));

  const handleConfirm = () => {
    if (!selectedContext) return;
    onSelectContext({
      type: selectedContext,
      boutique_id: selectedContext === 'boutique' ? selectedBoutique
        : selectedContext === 'central' ? (centralBoutique?.id || 1) : null,
      boutique_nom: selectedContext === 'boutique'
        ? boutiques.find(b => b.id === selectedBoutique)?.nom
        : selectedContext === 'central' ? (centralBoutique?.nom || 'Central') : 'Atelier',
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 100%)' }}>

      {/* Subtle gold particles effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-20">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="absolute rounded-full"
            style={{
              width: 2 + Math.random() * 3, height: 2 + Math.random() * 3,
              background: '#d4af37',
              left: `${10 + Math.random() * 80}%`, top: `${10 + Math.random() * 80}%`,
              animation: `pulse ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }} />
        ))}
      </div>

      <div className="max-w-4xl w-full relative z-10">
        {/* Logo & Brand */}
        <div className="text-center mb-16">
          {logoUrl ? (
            <img src={getServerUrl(logoUrl)}
              alt="Logo" className="h-20 mx-auto mb-6 drop-shadow-2xl" />
          ) : (
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #d4af37, #f5e6a3, #d4af37)', boxShadow: '0 0 40px rgba(212,175,55,0.3)' }}>
              <Gem className="h-10 w-10 text-gray-900" />
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-light tracking-[0.2em] uppercase mb-3"
            style={{ color: '#d4af37', fontFamily: "'Georgia', 'Times New Roman', serif", textShadow: '0 0 30px rgba(212,175,55,0.2)' }}>
            {nomEntreprise || 'Bijouterie'}
          </h1>
          {slogan && (
            <p className="text-sm tracking-[0.3em] uppercase" style={{ color: '#8a8a8a' }}>
              {slogan}
            </p>
          )}
          <div className="w-24 h-px mx-auto mt-6" style={{ background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        </div>

        {/* Context cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {contexts.map((ctx) => {
            const Icon = ctx.icon;
            const sel = selectedContext === ctx.id;
            const disabled = isDisabled(ctx.id);
            return (
              <button key={ctx.id}
                onClick={() => !disabled && setSelectedContext(ctx.id)}
                disabled={disabled}
                className="group relative overflow-hidden rounded-xl transition-all duration-500"
                style={{
                  background: disabled ? '#0d0d10' : sel ? 'linear-gradient(135deg, #1a1a2e, #2a2a3e)' : '#111118',
                  border: disabled ? '1px solid #1a1a1a' : sel ? '1px solid #d4af37' : '1px solid #2a2a3a',
                  boxShadow: sel && !disabled ? '0 0 30px rgba(212,175,55,0.15), inset 0 1px 0 rgba(212,175,55,0.1)' : 'none',
                  opacity: disabled ? 0.35 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}>
                {/* Gold line top */}
                <div className="h-0.5 w-full transition-all duration-500"
                  style={{ background: sel && !disabled ? 'linear-gradient(90deg, transparent, #d4af37, transparent)' : 'transparent' }} />
                <div className="p-8 text-center">
                  {disabled && (
                    <div className="absolute top-3 right-3">
                      <span className="text-xs text-gray-600">🔒</span>
                    </div>
                  )}
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300"
                    style={{
                      background: sel && !disabled ? 'linear-gradient(135deg, #d4af37, #f5e6a3)' : '#1e1e2e',
                      border: sel && !disabled ? 'none' : '1px solid #333',
                    }}>
                    <Icon className="h-7 w-7" style={{ color: sel && !disabled ? '#0a0a0a' : '#555' }} />
                  </div>
                  <h3 className="text-lg font-light tracking-[0.15em] uppercase mb-2 transition-colors duration-300"
                    style={{ color: sel && !disabled ? '#d4af37' : disabled ? '#444' : '#999', fontFamily: "'Georgia', serif" }}>
                    {ctx.name}
                  </h3>
                  <p className="text-xs tracking-wider" style={{ color: disabled ? '#333' : '#666' }}>{ctx.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Boutique selection */}
        {selectedContext === 'boutique' && (
          <div className="rounded-xl p-8 mb-10" style={{ background: '#111118', border: '1px solid #2a2a3a' }}>
            <h3 className="text-center text-sm tracking-[0.2em] uppercase mb-6" style={{ color: '#d4af37', fontFamily: "'Georgia', serif" }}>
              Selectionnez votre boutique
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {boutiquesList.map(b => {
                const sel = selectedBoutique === b.id;
                return (
                  <button key={b.id} onClick={() => setSelectedBoutique(b.id)}
                    className="p-5 rounded-lg transition-all duration-300"
                    style={{
                      background: sel ? '#1a1a2e' : 'transparent',
                      border: sel ? '1px solid #d4af37' : '1px solid #2a2a3a',
                    }}>
                    <Store className="h-6 w-6 mx-auto mb-2" style={{ color: sel ? '#d4af37' : '#555' }} />
                    <div className="text-sm font-medium" style={{ color: sel ? '#d4af37' : '#aaa' }}>{b.nom}</div>
                    {b.ville && <div className="text-xs mt-1" style={{ color: '#666' }}>{b.ville}</div>}
                  </button>
                );
              })}
              {boutiquesList.length === 0 && (
                <p className="text-center col-span-3 py-4" style={{ color: '#555' }}>Aucune boutique configuree</p>
              )}
            </div>
          </div>
        )}

        {/* Enter button */}
        <div className="flex justify-center">
          <button onClick={handleConfirm}
            disabled={!selectedContext || (selectedContext === 'boutique' && !selectedBoutique)}
            className="px-14 py-4 rounded-lg text-sm tracking-[0.25em] uppercase font-medium transition-all duration-500 disabled:opacity-20 disabled:cursor-not-allowed"
            style={{
              background: selectedContext && (selectedContext !== 'boutique' || selectedBoutique)
                ? 'linear-gradient(135deg, #d4af37, #f5e6a3, #d4af37)' : '#333',
              color: selectedContext && (selectedContext !== 'boutique' || selectedBoutique) ? '#0a0a0a' : '#666',
              boxShadow: selectedContext && (selectedContext !== 'boutique' || selectedBoutique)
                ? '0 4px 30px rgba(212,175,55,0.3)' : 'none',
              fontFamily: "'Georgia', serif",
            }}>
            Entrer
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <div className="w-16 h-px mx-auto mb-4" style={{ background: 'linear-gradient(90deg, transparent, #333, transparent)' }} />
          <p className="text-xs tracking-wider mb-3" style={{ color: '#444' }}>Systeme de gestion bijouterie</p>
          {user && (
            <div className="flex items-center justify-center gap-3 text-xs" style={{ color: '#666' }}>
              <span>Connecte : <span style={{ color: '#999' }}>{user.username}</span> ({user.role})</span>
              <button onClick={logout} className="flex items-center gap-1 px-3 py-1 rounded hover:bg-white/5 transition-colors" style={{ color: '#d4af37' }}>
                <LogOut className="h-3 w-3" /><span>Changer de compte</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContextSelector;
